import {
  guid,
  guidNumber,
  getFieldGroupByType,
  getOperationType,
  isTrueStringMatch,
  legacyUpdateScreenIndex,
} from 'apps/builder/lib/utils'

import { lib } from '@gocanvas/client'
const {
  getFieldDefaults,
  LOOP_SHEET_TYPE_IDS,
  LOOP_SHEET_TYPE_ID,
  SINGLE_CHOICE_DROPDOWN_STYLES,
} = lib

import { initialSettingState } from 'apps/builder/store/reducers/settings/appSettings'
import { sanitizeInput } from 'apps/builder/lib/utils'

// extractByKey :: Array -> Array
const extractByKey = (group = [], key) => {
  const combinedGroup = group.reduce(
    (combined, obj = {}) => combined.concat(obj[key]),
    []
  )
  return combinedGroup
}

// extractSheets :: Array -> Array
const extractSheets = sections =>
  extractByKey(sections, 'sheets').map(({ id, ...sheet }) => ({
    ...sheet,
    id: `${id}`,
  }))

export const convertToBuilderType = (type, style) => {
  if (type === 'Summary') return 'Sum'

  if (
    type === 'Single Choice' &&
    SINGLE_CHOICE_DROPDOWN_STYLES.includes(style)
  ) {
    return 'Dropdown'
  }

  return type
}

const extractEntries = sheets => {
  // collect table sheets
  const loopSheetIds = new Set()
  const subSectionParentMap = new Map()
  const sheetsToSectionsMap = new Map()

  sheets.forEach(({ multi_section, sheet_type_id, id, section_id }) => {
    if (LOOP_SHEET_TYPE_IDS.includes(sheet_type_id)) {
      loopSheetIds.add(`${id}`)
    }

    if (multi_section && sheet_type_id === LOOP_SHEET_TYPE_ID) {
      subSectionParentMap.set(
        `${multi_section?.id ?? multi_section}`,
        String(id)
      )
    }

    sheetsToSectionsMap.set(`${id}`, `${section_id}`)
  })

  // children of a table's multi_sections need the parent sheet_id
  const getParentSheetId = sheet_id => {
    const section_id = sheetsToSectionsMap.get(`${sheet_id}`)

    if (section_id && subSectionParentMap.has(`${section_id}`)) {
      return String(subSectionParentMap.get(`${section_id}`))
    }

    return `${sheet_id}`
  }

  const entries = extractByKey(sheets, 'entries')
  const entryLookup = new Map()

  const transformedEntries = entries.map(({ sheet_id, ...entry }) => {
    const type = convertToBuilderType(entry.type, entry.style)
    const { reference_data_id, reference_data_column, dependent_entry } = entry

    let extractedEntry = {
      ...entry,
      type: type,
      sheet_id: `${sheet_id}`,
      // any field on a LOOP_SHEET_TYPE_IDS sheet is a keyfield
      keyField: loopSheetIds.has(`${sheet_id}`),
    }

    // clean out partial setups
    if (!reference_data_id && reference_data_column) {
      extractedEntry.reference_data_id = null
      extractedEntry.reference_data_column = null
      extractedEntry.dependent_entry = null
    }

    if (reference_data_id && reference_data_column && dependent_entry) {
      const { guid, sheet_id: dependentEntrySheetId } =
        entries.find(entry => entry.guid === dependent_entry?.guid) || {}
      if (guid) {
        extractedEntry.dependent_entry = {
          ...dependent_entry,
          sheet_id: getParentSheetId(dependentEntrySheetId),
        }
      } else {
        // remove dependent_entry if it doesn't exist
        extractedEntry = {
          ...extractedEntry,
          dependent_entry: null,
        }
      }
    }
    entryLookup.set(entry.guid, extractedEntry)
    return extractedEntry
  })
  return [transformedEntries, entryLookup]
}

const extractConditions = (sheets, entries) => {
  const sheetConditions = extractByKey(sheets, 'conditions')
  const linkedSheetConditions = sheetConditions.map(
    ({ entry, ...condition }) => {
      return {
        entry: entry.guid, // the field that the condition depends on
        entrySheetId: entry.sheet_id, // ^ the sheet that field belongs to
        ...condition,
      }
    }
  )

  const linked_conditions = entries.flatMap(
    ({ guid, sheet_id, conditions = [] }) =>
      conditions.map(({ entry, ...condition }) => {
        return {
          parentEntryGuid: guid, // the field with the condition
          entry: entry.guid, // the field that the condition depends on
          entrySheetId: String(entry.sheet_id), //  ^ the sheet that field belongs to
          ...condition,
          sheet_id: String(sheet_id), // the sheet id of the field with the condition
        }
      })
  )
  return [...linked_conditions, ...linkedSheetConditions]
}

const extractOperations = (entries, entryLookupByGuid) =>
  entries.flatMap(({ guid, sheet_id, operations = [] }) =>
    operations.map(({ value_entry, operation_type_id, ...operation }, i) => {
      return {
        parentEntryGuid: value_entry ? value_entry.guid : value_entry,
        parentEntrySheetId:
          value_entry?.guid &&
          entryLookupByGuid.get(value_entry.guid)?.sheet_id,
        entry: guid,
        sheet_id: String(sheet_id),
        operation_type_id,
        operation_type: getOperationType(operation_type_id),
        index: i,
        ...operation,
      }
    })
  )

const extractAppMeta = ({ sections = [], ...appMeta }) => ({ ...appMeta })
const extractSheetMeta = ({
  entries = [],
  conditions,
  multi_section,
  ...sheet
}) => {
  return {
    ...sheet,
    multi_section: multi_section ? multi_section.id : multi_section,
  }
}

export const NON_BRANDED_URL = 'https://www.example.com'
const modifyEntry = ({ entry }, isWhiteLabeled = false) => {
  if (entry.type === 'Summary') {
    return {
      type: 'Sum',
      description: 'Sum',
      mask: '2',
    }
  }
  if (isWhiteLabeled && entry.type === 'Link') {
    return {
      default: NON_BRANDED_URL,
    }
  }
  return {}
}

const calculateSectionDepth = (sectionId, childToParentMap) => {
  let depth = 0
  let currentId = sectionId
  while (childToParentMap[currentId]) {
    currentId = childToParentMap[currentId]
    depth++
  }
  return depth
}

const getSectionDepth = nestedSheets => {
  const sectionChildToParentMap = nestedSheets.reduce((acc, sheet) => {
    acc[sheet.multi_section.id] = sheet.section_id
    return acc
  }, {})

  return nestedSheets.reduce((acc, sheet) => {
    const { multi_section } = sheet
    const depth = calculateSectionDepth(
      multi_section.id,
      sectionChildToParentMap
    )
    acc[multi_section.id] = depth
    return acc
  }, {})
}

const extractSections = ({ sections = [] }, extractedSheets) => {
  const nestedSheets = extractedSheets.filter(sheet => sheet.multi_section)
  const sectionDepthMap = getSectionDepth(nestedSheets)

  return sections.map(section => {
    const { sheets = [], ...sectionMeta } = section

    return {
      ...sectionMeta,
      depth: sectionDepthMap[section.id] || 0,
    }
  })
}

export const unpackType =
  isWhiteLabeled =>
  ({ entry_type }) => {
    const fieldDefaults = getFieldDefaults(entry_type.type)
    const modifiedType = modifyEntry({ entry: entry_type }, isWhiteLabeled)

    return {
      ...fieldDefaults,
      ...entry_type,
      ...modifiedType,
      fieldGroup: getFieldGroupByType(entry_type.type),
    }
  }

export const unpackOperationType = ({ operation_type: type }) => {
  const { id } = type

  return {
    operation_type: getOperationType(id),
    ...type,
  }
}

export const unpackConditionType = ({ condition_type }) => condition_type

export const unpackReferenceImageType = ({ reference_image: { id, name } }) => {
  return {
    id,
    name,
  }
}

export const unpackApp = (rawApp, defaultValues) => {
  if (rawApp) {
    const sheets = extractSheets(rawApp.sections)
    const [entries, entryLookupByGuid] = extractEntries(sheets)
    const sections = extractSections(rawApp, sheets)
    const firstSection = sections.find(
      ({ position, section_type_id }) =>
        position === 0 && section_type_id === 10
    )
    const { data_source_context_params = [], data_source_contexts = [] } = rawApp
    return {
      entities: {
        app: {
          ...extractAppMeta(rawApp),
          defaultAppName: defaultValues.name,
          logo: defaultValues.logo || '',
          builderVersion: '3',
        },
        entries: entries.map(({ conditions, operations, ...entry }) => entry),
        sections,
        sheets: sheets.map(extractSheetMeta),
        conditions: extractConditions(sheets, entries),
        operations: extractOperations(entries, entryLookupByGuid),
        dataSourceContextParams: data_source_context_params,
      },
      dataSourceContexts: data_source_contexts,
      settings: {
        ...initialSettingState,
        ...defaultValues.settings,
        activeSheet: firstSection
          ? sheets.find(({ section_id }) => section_id === firstSection.id).id
          : '',
      },
    }
  } else {
    const sectionGuid = guidNumber()
    const sheetGuid = guidNumber()

    return {
      entities: {
        app: {
          name: defaultValues.name,
          defaultAppName: defaultValues.name,
          version: 1,
          status: 'new',
          placeholder: 'Type App Name',
        },
        entries: [],
        sections: [
          {
            id: sectionGuid,
            description: 'Default Screen 1',
            placeholder: 'Type screen name',
            position: 0,
            section_type_id: 10,
            depth: 0,
          },
        ],
        sheets: [
          {
            id: sheetGuid,
            section_id: sectionGuid,
            description: '',
            placeholder: 'Type screen name',
            position: 0,
            sheet_type_id: 11,
          },
        ],
      },
      settings: {
        ...initialSettingState,
        ...defaultValues.settings,
        activeSheet: sheetGuid,
      },
    }
  }
}

const removeTempValues =
  (keys = []) =>
  entry => {
    var filter = ([key, value]) => {
      if (keys.includes(key)) {
        return !`${value}`.includes(`temp-`)
      }
      return true
    }
    const entries = Object.entries(entry)
    const filtered = Object.fromEntries(entries.filter(filter))
    return filtered
  }

const assembleEntryCondition = ({
  editing,
  entrySheetId,
  parentEntryGuid,
  sheet_id,
  ...condition
}) => {
  return {
    ...condition,
    entry: {
      guid: condition.entry,
      //sheet_id: entrySheetId // unsure if needed: bv2 does not send on a new save, includes it on later saves
    },
    sheet_id: null, // overwrite any existing sheet_id, which shouldn't be here
  }
}

const assembleSheetCondition = ({
  editing,
  entrySheetId,
  sheet_id, // should not be needed in the payload, as bv2 seems to infer it from position
  parentEntryGuid,
  ...condition
}) => {
  return {
    ...condition,
    parent_entry_id: null, // overwrite any existing parent_entry_id, which shouldn't be here
    entry: {
      guid: condition.entry,
      //sheet_id: entrySheetId//sheet_id: entrySheetId // unsure if needed: bv2 does not send on a new save, includes it on later saves
    },
  }
}

// filters out props used in store
const assembleOperation = ({
  parentEntryGuid,
  value_entry_id,
  index,
  entry_id,
  entry,
  operation_type,
  original_type_id,
  sheet_id,
  parentEntrySheetId,
  ...operation
}) => {
  return {
    ...operation,
    value_entry: {
      guid: parentEntryGuid,
    },
  }
}

const assembleEntries = (entries, conditions, operations) => {
  return entries.map(entry => {
    const {
      dependent_entry,
      reference_data_id,
      reference_data_column,
      data_source_context_id,
      live_data_column,
    } = entry
    const entryConditions = conditions
      .filter(
        ({ parentEntryGuid, condition_type_id }) =>
          parentEntryGuid == entry.guid && condition_type_id
      )
      .map(assembleEntryCondition)
    const entryOperations = operations
      .filter(operation => operation.entry === entry.guid)
      .map(assembleOperation)

    const type = entry.type == 'Sum' ? 'Summary' : entry.type
    const assembledEntry = {
      ...entry,
      type: type,
      operations: entryOperations.sort((a, b) => a.index - b.index),
      conditions: entryConditions,
    }

    if (!data_source_context_id && !live_data_column) {
      assembledEntry.data_source_context_id = null
      assembledEntry.live_data_column = null
      assembledEntry.live_data_source_id = null
    } else if (data_source_context_id && live_data_column && dependent_entry) {
      const dependentGuid = dependent_entry?.guid
      const dependentEntryExists =
        dependentGuid && entries.some(entry => entry.guid === dependentGuid)
      assembledEntry.dependent_entry = dependentEntryExists
        ? { guid: dependentGuid }
        : null
    }

    // clean out any partial setups, ensure dependent_entry has a legit guid
    if (!reference_data_id && reference_data_column) {
      assembledEntry.reference_data_id = null
      assembledEntry.reference_data_column = null
      assembledEntry.dependent_entry = null
    } else if (reference_data_id && reference_data_column && dependent_entry) {
      const dependentGuid = dependent_entry?.guid
      const dependentEntryExists =
        dependentGuid && entries.some(entry => entry.guid === dependentGuid)
      assembledEntry.dependent_entry = dependentEntryExists
        ? { guid: dependentGuid }
        : null
    }

    if (type === 'Static Image') {
      // Having a summary_entry object in the JSON for static image entries causes issues in the backend
      delete assembledEntry.summary_entry
    }

    return assembledEntry
  })
}

const compareSheetIds =
  sheetId =>
  ({ sheet_id }) =>
    sheet_id && isTrueStringMatch(sheet_id, sheetId)

const compareOnlySheetConditionIds =
  sheetId =>
  ({ sheet_id, parentEntryGuid, condition_type_id }) =>
    !parentEntryGuid &&
    condition_type_id &&
    isTrueStringMatch(sheet_id, sheetId)

const assembleSheets = (sheets, entries, sections, conditions) => {
  return sheets.map(
    ({ description, multi_section: multi_section_id, ...sheet }, i) => {
      const newSheet = {
        ...sheet,
        description: sanitizeInput(description) || `New Screen ${i}`,
      }
      const multi_section = sections.find(
        section => section.id === multi_section_id
      )
      return {
        ...newSheet,
        multi_section,
        entries: entries
          .filter(compareSheetIds(sheet.id))
          .map(({ type, label, ...entry }) => {
            const newEntry = {
              ...entry,
              label: sanitizeInput(label) || `New ${type}`,
            }
            return newEntry
          }),
        conditions: conditions
          .filter(compareOnlySheetConditionIds(sheet.id))
          .map(assembleSheetCondition),
      }
    }
  )
}

const assembleSections = (sections, sheets) => {
  return sections
    .map(section => ({
      ...section,
      sheets: sheets
        .filter(({ section_id }) => section_id === section.id)
        .map(removeTempValues(['id', 'section_id'])),
    }))
    .map(removeTempValues(['id']))
}

export const assembleForm = ({
  app,
  entries,
  sheets,
  sections,
  conditions,
  operations,
  dataSourceContextParams = [],
  dataSourceContexts = [],
}) => {
  const appData =
    app.name.length > 0 ? { ...app } : { ...app, name: app.defaultAppName }
  const form = {
    ...appData,
    name: sanitizeInput(appData.name),
    sections: assembleSections(
      sections,
      assembleSheets(
        sheets,
        assembleEntries(entries, conditions, operations),
        sections,
        conditions
      )
    ),
  }

  return {
    form: legacyUpdateScreenIndex(form),
    // filter out any contexts that aren't used
    data_source_contexts: dataSourceContexts.filter(
      context =>
        context.id &&
        entries.some(entry => entry.data_source_context_id === context.id)
    ),
    data_source_context_params: dataSourceContextParams.filter(
      param => param.id && entries.some(entry => entry.id === param.entry_id)
    ),
    builderVersion: app.builderVersion,
    return_full_form: true,
  }
}
